import { useEffect, useState } from "react";
import styled from "styled-components";
import { Divider, Padding, Row, SizedBox } from "../../../common/components/CommonBaseComponents";
import StatisticalAnalysisUsageCard from "../components/StatisticalAnalysisUsageCard";
import CustomGraphCard, { weekdayButtonSectionValue } from "../../../common/components/card/CustomGraphCard";
import UsageRateTable from "../../../common/components/table/UsageRateTable";
import { C1Text, T3Text } from "../../../common/components/text/CustomText";
import { CustomLegend } from "../../../common/components/chart/CustomGrpahCommonComponents";
import { useNavigate } from "react-router-dom";
import { CustomTable, LinkTd } from "../../../common/components/table/CustomTable";
import Translate from "../../../common/translate/Translate";
import { Theme } from "../../../common/Theme";
import { useDevice } from "../../../common/hooks/useDevice";
import { useWindowSize } from "../../../common/hooks/useWindowSize";
import RevenueService from "../../../service/RevenueService";

const WebTextCardGrid = styled.div`
    display: grid;
    grid-template-columns: ${({ $rowItemCount }) => ($rowItemCount === 2 ? "repeat(2, 1fr)" : "repeat(4, 1fr)")};
    gap: 17px;
    height: ${({ $rowItemCount }) => ($rowItemCount === 2 ? "357" : "170")}px;
`;

const WebGraphGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 17px;
    height: 426px;
`;

/**
 * dateRange 값("Today", "7days", "30days", "Custom")을 API용 startDate/endDate(yyyy-MM-dd)로 변환
 */
const getDateParams = (dateRange) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    const endDate = fmt(today);
    let start = new Date(today);

    if (dateRange === "Today") {
        // 어제 ~ 오늘 (기본값)
        start.setDate(start.getDate() - 1);
    } else if (dateRange === "7days") {
        start.setDate(start.getDate() - 7);
    } else if (dateRange === "30days") {
        start.setDate(start.getDate() - 30);
    } else {
        start.setDate(start.getDate() - 7);
    }
    return { startDate: fmt(start), endDate };
};

function StatisticalAnalysisUsageTabContent({ dateRange }) {
    const service = new RevenueService();
    const navigation = useNavigate();

    const [deviceData, setDeviceData] = useState(null);
    const [usageDailyData, setUsageDailyData] = useState([]);
    const [usageWeeklyData, setUsageWeeklyData] = useState([]);
    const [shopList, setShopList] = useState([]);
    const [usageMainText, setUsageMainText] = useState("0");

    const fetchData = (dateRangeValue) => {
        const { startDate, endDate } = getDateParams(dateRangeValue);

        // 디바이스 현황
        service
            .getUsageDevice({ startDate, endDate })
            .then((result) => {
                setDeviceData(result);
            })
            .catch((error) => {
                console.error("Error fetching usage device:", error);
            });

        // 일별 사용량 그래프 (spec 04.01.05.10)
        service
            .getUsageDay({ startDate, endDate })
            .then((result) => {
                const graphData = (result || []).map((item) => ({
                    name: item.day,
                    Washer: item.washer || 0,
                    Dryer: item.dryer || 0,
                    Combo: item.combo || 0,
                }));
                setUsageDailyData(graphData);
                const total = (result || []).reduce((sum, item) => sum + (item.total || 0), 0);
                setUsageMainText(total.toLocaleString());
            })
            .catch((error) => {
                console.error("Error fetching usage day:", error);
            });

        // 요일별 사용량 (spec 04.01.05.09: dayWeek)
        service
            .getUsageWeek({ startDate, endDate })
            .then((result) => {
                const graphData = (result || []).map((item) => ({
                    name: item.dayWeek,
                    Washer: item.washer || 0,
                    Dryer: item.dryer || 0,
                    Combo: item.combo || 0,
                }));
                setUsageWeeklyData(graphData);
            })
            .catch((error) => {
                console.error("Error fetching usage week:", error);
            });

        // 매장별 사용량 목록 (spec 04.01.05.08: 플랫 리스트)
        service
            .getUsageShop({ startDate, endDate })
            .then((result) => {
                const shops = (result || []).map((item, index) => ({
                    number: String(index + 1),
                    shopCode: item.shopCode,
                    shopName: item.shopName,
                    total: item.total,
                    washer: item.washer,
                    dryer: item.dryer,
                    combo: item.combo,
                }));
                setShopList(shops);
            })
            .catch((error) => {
                console.error("Error fetching usage shop list:", error);
            });
    };

    useEffect(() => {
        fetchData(dateRange || "7days");
    }, [dateRange]);

    const tableHeaders = [
        {
            key: "number",
            label: Translate("common.number"),
        },
        {
            key: "shopCode",
            label: Translate("store.shopCode"),
            render: (row) => (
                <LinkTd
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigation(`/shops/${row.shopCode}`);
                    }}
                >
                    {row.shopCode}
                </LinkTd>
            ),
        },
        {
            key: "shopName",
            label: Translate("store.shopName"),
        },
        {
            key: "total",
            label: `${Translate("common.total")}(Cnt)`,
            render: (row) => <>{row.total?.toLocaleString()}</>,
        },
        {
            key: "washer",
            label: `${Translate("common.washer")}(Cnt)`,
            render: (row) => <>{row.washer?.toLocaleString()}</>,
        },
        {
            key: "dryer",
            label: `${Translate("common.dryer")}(Cnt)`,
            render: (row) => <>{row.dryer?.toLocaleString()}</>,
        },
        {
            key: "combo",
            label: `${Translate("common.combo")}(Cnt)`,
            render: (row) => <>{row.combo?.toLocaleString()}</>,
        },
    ];

    const currentDevice = useDevice();
    const { width } = useWindowSize();

    const revenueByDateAndWeekdaysGraphCard = (
        <CustomGraphCard
            dailyData={usageDailyData}
            weeklyData={usageWeeklyData}
            title={Translate("revenue.usageByDateAndWeekdays")}
            mainText={usageMainText}
            isBarChart={true}
            buttonSectionValue={weekdayButtonSectionValue}
        />
    );

    const usageRateByTimePeriodTableCard = (
        <SizedBox
            border={`1px solid ${Theme.color.gray250}`}
            borderRadius={"16px"}
            width={"100%"}
            height={"100%"}
            backgroundColor={"transparent"}
            padding={"20px"}
            justifyContent={"flex-start"}
            alignItem={"flex-start"}
        >
            <Row justifyContent={"space-between"} alignItem={"flex-start"} width={"100%"}>
                <C1Text>{Translate("revenue.usageRateByTimePeriod")}</C1Text>
                <CustomLegend
                    paddingTop={"0px"}
                    payload={[
                        { value: "0~4%", color: Theme.color.slate100 },
                        { value: "4~8%", color: Theme.color.violet200 },
                        { value: "8~12%", color: Theme.color.violet400 },
                        { value: "12% ↑", color: Theme.color.indigo500 },
                    ]}
                ></CustomLegend>
            </Row>
            <UsageRateTable />
        </SizedBox>
    );
    const totalDevice = deviceData?.totalDevice || {};
    const activeDevice = deviceData?.activeDevice || {};
    const notAvailableDevice = deviceData?.notAvailableDevice || {};
    const freeCycleDevice = deviceData?.freeCycle || {};
    const totalDeviceCount = totalDevice.total || 0;

    const totalDeviceCard = (
        <StatisticalAnalysisUsageCard
            type={"TotalDevice"}
            mainValue={totalDeviceCount}
            totalValue={totalDeviceCount}
            washerValue={totalDevice.washer || 0}
            dryValue={totalDevice.dryer || 0}
            comboValue={totalDevice.combo || 0}
        />
    );
    const activeDeviceCard = (
        <StatisticalAnalysisUsageCard
            type={"ActiveDevice"}
            mainValue={activeDevice.total || 0}
            totalValue={totalDeviceCount}
            washerValue={activeDevice.washer || 0}
            dryValue={activeDevice.dryer || 0}
            comboValue={activeDevice.combo || 0}
        />
    );
    const notAvailableDeviceCard = (
        <StatisticalAnalysisUsageCard
            type={"NotAvailableDevice"}
            mainValue={notAvailableDevice.total || 0}
            totalValue={totalDeviceCount}
            washerValue={notAvailableDevice.washer || 0}
            dryValue={notAvailableDevice.dryer || 0}
            comboValue={notAvailableDevice.combo || 0}
        />
    );
    const freeCycleCard = (
        <StatisticalAnalysisUsageCard
            mainValue={freeCycleDevice.total || 0}
            type={"FreeCycle"}
            totalValue={totalDeviceCount}
            washerValue={freeCycleDevice.washer || 0}
            dryValue={freeCycleDevice.dryer || 0}
            comboValue={freeCycleDevice.combo || 0}
        />
    );
    const tableArea = (
        <>
            <SizedBox height={"16px"} width={"100%"} />
            <Padding padding={"0 21px"}>
                <T3Text>{Translate("revenue.usageCountByShops")}</T3Text>
            </Padding>
            <Divider thickness="0" marginTop="12px" />
            <CustomTable headers={tableHeaders} data={shopList} />
        </>
    );

    if (width <= 600) {
        return (
            <>
                {totalDeviceCard}
                <SizedBox height={"17px"} />
                {activeDeviceCard}
                <SizedBox height={"17px"} />
                {notAvailableDeviceCard}
                <SizedBox height={"17px"} />
                {freeCycleCard}
                <SizedBox height={"17px"} />
                {revenueByDateAndWeekdaysGraphCard}
                <SizedBox height={"17px"} />
                {usageRateByTimePeriodTableCard}
                {tableArea}
            </>
        );
    }
    if (width <= 1220) {
        return (
            <>
                <WebTextCardGrid $rowItemCount={2}>
                    {totalDeviceCard}
                    {activeDeviceCard}
                    {notAvailableDeviceCard}
                    {freeCycleCard}
                </WebTextCardGrid>
                <SizedBox height={"17px"} />
                {revenueByDateAndWeekdaysGraphCard}
                <SizedBox height={"17px"} />
                {usageRateByTimePeriodTableCard}
                {tableArea}
            </>
        );
    }

    return (
        <>
            <WebTextCardGrid $rowItemCount={4}>
                {totalDeviceCard}
                {activeDeviceCard}
                {notAvailableDeviceCard}
                {freeCycleCard}
            </WebTextCardGrid>
            <SizedBox height={"17px"} />
            <WebGraphGrid>
                {revenueByDateAndWeekdaysGraphCard}
                {usageRateByTimePeriodTableCard}
            </WebGraphGrid>
            {tableArea}
        </>
    );
}

export default StatisticalAnalysisUsageTabContent;
